import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from './lib/theme.js';
import { AuthProvider, useAuth } from './lib/auth.js';
import { ToastProvider } from './components/ui/index.js';
import { LoadingState } from './components/ui/index.js';
import { AppShell } from './components/AppShell.js';
import { LoginPage } from './pages/LoginPage.js';
import { OverviewPage } from './pages/OverviewPage.js';
import { OrganisationsPage } from './pages/OrganisationsPage.js';
import { SignupsPage } from './pages/SignupsPage.js';
import { ModulesPage } from './pages/ModulesPage.js';
import { OrganisationDetailPage } from './pages/OrganisationDetailPage.js';
import { OrgFormPage } from './pages/OrgFormPage.js';
import { TestHospitalPage } from './pages/TestHospitalPage.js';
import { AuditPage } from './pages/AuditPage.js';
import { OnboardingPage } from './pages/OnboardingPage.js';
import { SupportPage } from './pages/SupportPage.js';
import { CaseDetailPage } from './pages/CaseDetailPage.js';
import { ApprovalsPage } from './pages/ApprovalsPage.js';
import { CommercialPage } from './pages/CommercialPage.js';
import { EntitlementsPage } from './pages/EntitlementsPage.js';
import { BillingPage } from './pages/BillingPage.js';
import { ProvisioningPage } from './pages/ProvisioningPage.js';
import { FleetPage } from './pages/FleetPage.js';
import { InstallationDetailPage } from './pages/InstallationDetailPage.js';
import { ReleasesPage } from './pages/ReleasesPage.js';
import { CampaignDetailPage } from './pages/CampaignDetailPage.js';
import { ReliabilityPage } from './pages/ReliabilityPage.js';
import { HomePage } from './pages/home/HomePage.js';
import { AnalyticsPage } from './pages/AnalyticsPage.js';
import { HealthPage } from './pages/HealthPage.js';
import { CompliancePage } from './pages/CompliancePage.js';
import { RiskPage } from './pages/RiskPage.js';
import { AutomationPage } from './pages/AutomationPage.js';
import { ReportsPage } from './pages/ReportsPage.js';
import { ReportDetailPage } from './pages/ReportDetailPage.js';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { loading, unauthenticated } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingState label="Checking session…" />;
  if (unauthenticated) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

function LandingRoute() {
  // Operators without overview.read (e.g. Auditor) land on their first
  // permitted surface instead of a permission-denied screen.
  const { hasPermission } = useAuth();
  if (hasPermission('overview.read')) return <OverviewPage />;
  // Roles without the shared overview land on their purpose-built home.
  return <Navigate to="/home" replace />;
}

export function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ToastProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                element={
                  <RequireAuth>
                    <AppShell />
                  </RequireAuth>
                }
              >
                <Route path="/" element={<LandingRoute />} />
                <Route path="/home" element={<HomePage />} />
                <Route path="/analytics" element={<AnalyticsPage />} />
                <Route path="/health" element={<HealthPage />} />
                <Route path="/compliance" element={<CompliancePage />} />
                <Route path="/risk" element={<RiskPage />} />
                <Route path="/automation" element={<AutomationPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/reports/:id" element={<ReportDetailPage />} />
                <Route path="/signups" element={<SignupsPage />} />
                <Route path="/modules" element={<ModulesPage />} />
                <Route path="/organisations" element={<OrganisationsPage />} />
                <Route path="/organisations/new" element={<OrgFormPage mode="create" />} />
                <Route path="/organisations/new-test-hospital" element={<TestHospitalPage />} />
                <Route path="/organisations/:id" element={<OrganisationDetailPage />} />
                <Route path="/organisations/:id/edit" element={<OrgFormPage mode="edit" />} />
                <Route path="/onboarding" element={<OnboardingPage />} />
                <Route path="/commercial" element={<CommercialPage />} />
                <Route path="/entitlements" element={<EntitlementsPage />} />
                <Route path="/billing" element={<BillingPage />} />
                <Route path="/provisioning" element={<ProvisioningPage />} />
                <Route path="/fleet" element={<FleetPage />} />
                <Route path="/fleet/:identifier" element={<InstallationDetailPage />} />
                <Route path="/releases" element={<ReleasesPage />} />
                <Route path="/releases/campaigns/:id" element={<CampaignDetailPage />} />
                <Route path="/reliability" element={<ReliabilityPage />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/support/:id" element={<CaseDetailPage />} />
                <Route path="/approvals" element={<ApprovalsPage />} />
                <Route path="/audit" element={<AuditPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
